import * as React from 'react';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import CSSMotion from '@rc-component/motion';
import { isReactRenderable, useDelayState } from '@rc-component/util';
import { clsx } from 'clsx';
import { isFunction } from '../../_util/is';
import { ConfigContext } from '../../config-provider';
import Progress from '../../progress';
import Tooltip from '../../tooltip';
const ListItem = React.forwardRef(({ prefixCls, className, style, classNames: itemClassNames, styles, locale, listType, file, items, progress: progressProps, iconRender, actionIconRender, itemRender, isImgUrl, showPreviewIcon, showRemoveIcon, showDownloadIcon, previewIcon: customPreviewIcon, removeIcon: customRemoveIcon, downloadIcon: customDownloadIcon, extra: customExtra, onPreview, hasPreview, onDownload, onClose, }, ref) => {
    // Status: which will ignore `removed` status
    const { status } = file;
    const [mergedStatus, setMergedStatus] = React.useState(status);
    React.useEffect(() => {
        if (status !== 'removed') {
            setMergedStatus(status);
        }
    }, [status]);
    // Delay to show the progress bar
    const [showProgress, setShowProgress] = useDelayState(false);
    React.useEffect(() => {
        setShowProgress(true, { ms: 300 });
    }, []);
    const iconNode = iconRender(file);
    let icon = React.createElement("div", { className: `${prefixCls}-icon` }, iconNode);
    if (listType === 'picture' || listType === 'picture-card' || listType === 'picture-circle') {
        if (mergedStatus === 'uploading' || (!file.thumbUrl && !file.url)) {
            const uploadingClassName = clsx(`${prefixCls}-list-item-thumbnail`, {
                [`${prefixCls}-list-item-file`]: mergedStatus !== 'uploading',
            });
            icon = React.createElement("div", { className: uploadingClassName }, iconNode);
        }
        else {
            const thumbnail = isImgUrl?.(file) ? (React.createElement("img", { src: file.thumbUrl || file.url, alt: file.name, className: `${prefixCls}-list-item-image`, crossOrigin: file.crossOrigin })) : (iconNode);
            const aClassName = clsx(`${prefixCls}-list-item-thumbnail`, {
                [`${prefixCls}-list-item-file`]: isImgUrl && !isImgUrl(file),
            });
            icon = (React.createElement("a", { className: aClassName, onClick: (e) => onPreview(file, e), href: file.url || file.thumbUrl, target: "_blank", rel: "noopener noreferrer" }, thumbnail));
        }
    }
    const listItemClassName = clsx(`${prefixCls}-list-item`, `${prefixCls}-list-item-${mergedStatus}`, itemClassNames?.item);
    const linkProps = typeof file.linkProps === 'string' ? JSON.parse(file.linkProps) : file.linkProps;
    const removeIcon = (isFunction(showRemoveIcon) ? showRemoveIcon(file) : showRemoveIcon)
        ? actionIconRender((isFunction(customRemoveIcon) ? customRemoveIcon(file) : customRemoveIcon) || (React.createElement(DeleteOutlined, null)), () => onClose(file), prefixCls, locale.removeFile, 
        // acceptUploadDisabled is true, only remove icon will follow Upload disabled prop
        // https://github.com/ant-design/ant-design/issues/46171
        true)
        : null;
    const downloadIcon = (isFunction(showDownloadIcon) ? showDownloadIcon(file) : showDownloadIcon) &&
        mergedStatus === 'done'
        ? actionIconRender((isFunction(customDownloadIcon) ? customDownloadIcon(file) : customDownloadIcon) || (React.createElement(DownloadOutlined, null)), () => onDownload(file), prefixCls, locale.downloadFile)
        : null;
    const downloadOrDelete = listType !== 'picture-card' && listType !== 'picture-circle' && (React.createElement("span", { key: "download-delete", className: clsx(`${prefixCls}-list-item-actions`, { picture: listType === 'picture' }) },
        downloadIcon,
        removeIcon));
    const extraContent = isFunction(customExtra) ? customExtra(file) : customExtra;
    const extra = isReactRenderable(extraContent) && (React.createElement("span", { className: `${prefixCls}-list-item-extra` }, extraContent));
    const listItemNameClass = clsx(`${prefixCls}-list-item-name`);
    const onPreviewKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPreview(file, e);
        }
    };
    const spanProps = hasPreview
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: (e) => onPreview(file, e),
            onKeyDown: onPreviewKeyDown,
        }
        : {};
    const fileName = file.url ? (React.createElement("a", { key: "view", target: "_blank", rel: "noopener noreferrer", className: listItemNameClass, title: file.name, ...linkProps, href: file.url, onClick: (e) => {
            linkProps?.onClick?.(e);
            if (!e.defaultPrevented) {
                onPreview(file, e);
            }
        } },
        file.name,
        extra)) : (React.createElement("span", { key: "view", className: listItemNameClass, ...spanProps, title: file.name },
        file.name,
        extra));
    const previewIcon = (isFunction(showPreviewIcon) ? showPreviewIcon(file) : showPreviewIcon) &&
        (file.url || file.thumbUrl) ? (React.createElement("a", { href: file.url || file.thumbUrl, target: "_blank", rel: "noopener noreferrer", onClick: (e) => onPreview(file, e), title: locale.previewFile, "aria-label": locale.previewFile || undefined }, isFunction(customPreviewIcon)
        ? customPreviewIcon(file)
        : customPreviewIcon || React.createElement(EyeOutlined, null))) : null;
    const pictureCardActions = (listType === 'picture-card' || listType === 'picture-circle') &&
        mergedStatus !== 'uploading' && (React.createElement("span", { className: `${prefixCls}-list-item-actions` },
        previewIcon,
        mergedStatus === 'done' && downloadIcon,
        removeIcon));
    const { getPrefixCls } = React.useContext(ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const dom = (React.createElement("div", { className: listItemClassName, style: styles?.item },
        icon,
        fileName,
        downloadOrDelete,
        pictureCardActions,
        showProgress && (React.createElement(CSSMotion, { motionName: `${rootPrefixCls}-fade`, visible: mergedStatus === 'uploading', motionDeadline: 2000 }, ({ className: motionClassName }) => {
            // show loading icon if upload progress listener is disabled
            const loadingProgress = 'percent' in file ? (React.createElement(Progress, { type: "line", percent: file.percent, "aria-label": file['aria-label'], "aria-labelledby": file['aria-labelledby'], ...progressProps })) : null;
            return (React.createElement("div", { className: clsx(`${prefixCls}-list-item-progress`, motionClassName) }, loadingProgress));
        }))));
    const message = file.response && typeof file.response === 'string'
        ? file.response
        : file.error?.statusText || file.error?.message || locale.uploadError;
    const item = mergedStatus === 'error' ? (React.createElement(Tooltip, { title: message, getPopupContainer: (node) => node.parentNode }, dom)) : (dom);
    return (React.createElement("div", { className: clsx(`${prefixCls}-list-item-container`, className), style: style, ref: ref }, itemRender
        ? itemRender(item, file, items, {
            download: onDownload.bind(null, file),
            preview: onPreview.bind(null, file),
            remove: onClose.bind(null, file),
        })
        : item));
});
export default ListItem;
